/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
import { A11yModule } from '@angular/cdk/a11y';
import { OverlayModule } from '@angular/cdk/overlay';
import { PortalModule } from '@angular/cdk/portal';
import { CdkScrollableModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { SvgIconComponent, provideSvgIcons } from '@ngneat/svg-icon';
import { TranslatePipe } from '@ngx-translate/core';

import { ButtonComponent } from '@shared/components/button';

import { appCalendar24Icon } from '@icons/calendar-24';
import { appChevronLeft24Icon } from '@icons/chevron-left-24';
import { appChevronRight24Icon } from '@icons/chevron-right-24';

import { MatCalendar, MatCalendarHeader } from './calendar';
import { MatCalendarBody } from './calendar-body';
import { MatCommonModule } from './core/common-behaviors';
import { MatDateRangeInput } from './date-range-input';
import { MatEndDate, MatStartDate } from './date-range-input-parts';
import { MatDateRangePicker } from './date-range-picker';
import { DateTimePartPipe } from './date-time-part.pipe';
import { MatDatepicker } from './datepicker';
import { MatDatepickerActions, MatDatepickerApply, MatDatepickerCancel } from './datepicker-actions';
import { MAT_DATEPICKER_SCROLL_STRATEGY_FACTORY_PROVIDER, MatDatepickerContent } from './datepicker-base';
import { MatDatepickerInput } from './datepicker-input';
import { MatDatepickerIntl } from './datepicker-intl';
import { MatDatepickerToggle, MatDatepickerToggleIcon } from './datepicker-toggle';
import { MatMonthView } from './month-view';
import { MatMultiYearView } from './multi-year-view';
import { MatYearView } from './year-view';

const icons = [appCalendar24Icon, appChevronLeft24Icon, appChevronRight24Icon];

@NgModule({
  imports: [CommonModule, OverlayModule, A11yModule, PortalModule, MatCommonModule, SvgIconComponent, ButtonComponent, TranslatePipe],
  exports: [
    CdkScrollableModule,
    MatCalendar,
    MatCalendarBody,
    MatDatepicker,
    MatDatepickerContent,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDatepickerToggleIcon,
    MatMonthView,
    MatYearView,
    MatMultiYearView,
    MatCalendarHeader,
    MatDateRangeInput,
    MatStartDate,
    MatEndDate,
    MatDateRangePicker,
    MatDatepickerActions,
    MatDatepickerCancel,
    MatDatepickerApply,
  ],
  declarations: [
    MatCalendar,
    MatCalendarBody,
    MatDatepicker,
    MatDatepickerContent,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDatepickerToggleIcon,
    MatMonthView,
    MatYearView,
    MatMultiYearView,
    MatCalendarHeader,
    MatDateRangeInput,
    MatStartDate,
    MatEndDate,
    MatDateRangePicker,
    MatDatepickerActions,
    MatDatepickerCancel,
    MatDatepickerApply,
    DateTimePartPipe,
  ],
  providers: [MatDatepickerIntl, MAT_DATEPICKER_SCROLL_STRATEGY_FACTORY_PROVIDER, provideSvgIcons(icons)],
})
export class MatDatepickerModule {}
